import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { Link } from "react-router-dom";

import Featurelist from "../Feature/FeatureProductlist/Featurelist";
import LoadingState from "../../common/LoadingState";

const SERVER_BASE_URL = "https://admin.monsinidress.com";

const DEFAULT_NAV_DATA = {
	PROM: {
		years: [2027, 2026, 2025],
	},
};

let navbarCache = null;
let searchCache = null;

function normalizeNavbarData(rawData) {
	if (!rawData || typeof rawData !== "object" || Array.isArray(rawData)) {
		return DEFAULT_NAV_DATA;
	}

	const normalized = {};

	Object.entries(rawData).forEach(([categoryName, categoryValue]) => {
		const key = String(categoryName).trim().toUpperCase();

		if (!key) {
			return;
		}

		const years = Array.isArray(categoryValue?.years)
			? [...new Set(categoryValue.years)]
					.map((year) => Number(year))
					.filter(Number.isFinite)
					.sort((a, b) => b - a)
			: [];

		normalized[key] = {
			years,
		};
	});

	return {
		...DEFAULT_NAV_DATA,
		...normalized,
	};
}

function getCategoryRoute(categoryName = "") {
	return String(categoryName)
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "");
}

const ChevronDownIcon = ({ className = "h-4 w-4" }) => (
	<svg
		viewBox="0 0 20 20"
		fill="currentColor"
		className={className}
		aria-hidden="true"
		focusable="false"
	>
		<path
			fillRule="evenodd"
			d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
			clipRule="evenodd"
		/>
	</svg>
);

const SearchIcon = ({ className = "h-6 w-6" }) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
		aria-hidden="true"
		focusable="false"
	>
		<circle cx="11" cy="11" r="8" />
		<path d="m21 21-4.35-4.35" />
	</svg>
);

const InstagramIcon = ({ className = "h-6 w-6" }) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		className={className}
		aria-hidden="true"
		focusable="false"
	>
		<rect x="3" y="3" width="18" height="18" rx="5" />
		<circle cx="12" cy="12" r="4" />
		<circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
	</svg>
);

const MenuIcon = ({ className = "h-8 w-8" }) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		className={className}
		aria-hidden="true"
		focusable="false"
	>
		<path d="M4 7h16M4 12h16M4 17h16" />
	</svg>
);

const CloseIcon = ({ className = "h-7 w-7" }) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		className={className}
		aria-hidden="true"
		focusable="false"
	>
		<path d="M6 6l12 12M18 6 6 18" />
	</svg>
);

const DropdownMenu = memo(function DropdownMenu({ title, options, loading }) {
	const [open, setOpen] = useState(false);

	const menuId = `azure-nav-${getCategoryRoute(title)}`;

	const handleBlur = useCallback((event) => {
		if (!event.currentTarget.contains(event.relatedTarget)) {
			setOpen(false);
		}
	}, []);

	return (
		<li
			className="relative font-poppins text-[15px] font-medium"
			onMouseEnter={() => setOpen(true)}
			onMouseLeave={() => setOpen(false)}
			onBlur={handleBlur}
		>
			<button
				type="button"
				onClick={() => setOpen((current) => !current)}
				className="flex items-center rounded px-3 py-2 font-medium uppercase tracking-wide text-black transition-colors duration-200 hover:text-[#1f4c94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94]"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
			>
				{title}
				<ChevronDownIcon
					className={`ml-1 h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</button>

			<div
				id={menuId}
				className={`absolute left-1/2 z-40 mt-0 w-48 -translate-x-1/2 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition-all duration-200 ${
					open ? "visible opacity-100" : "invisible opacity-0"
				}`}
			>
				<div className="py-1">
					{loading && options.length === 0 ? (
						<div className="px-4 py-3 text-center text-sm text-gray-500">
							Loading collections...
						</div>
					) : null}

					{options.map((option) => (
						<Link
							key={option.value}
							to={option.value}
							aria-label={option.ariaLabel}
							onClick={() => setOpen(false)}
							className="block w-full border-b border-gray-100 px-4 py-3 text-center text-sm text-gray-700 transition-colors duration-200 last:border-b-0 hover:bg-gray-50 hover:text-[#1f4c94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1f4c94]"
						>
							{option.label}
						</Link>
					))}
				</div>
			</div>
		</li>
	);
});

function Navbar() {
	const [isScrolled, setIsScrolled] = useState(false);
	const [showSearch, setShowSearch] = useState(false);
	const [showMenu, setShowMenu] = useState(false);
	const [searchData, setSearchData] = useState(searchCache || []);
	const [searchLoading, setSearchLoading] = useState(false);
	const [searchError, setSearchError] = useState("");
	const [apiData, setApiData] = useState(navbarCache || DEFAULT_NAV_DATA);
	const [navLoading, setNavLoading] = useState(!navbarCache);
	const navbarRequestedRef = useRef(false);
	const searchRequestedRef = useRef(Boolean(searchCache));

	const fetchWithTimeout = useCallback(
		async (url, options, timeoutMs = 7000) => {
			const controller = new AbortController();
			const timer = window.setTimeout(() => controller.abort(), timeoutMs);

			try {
				return await fetch(url, {
					...options,
					signal: controller.signal,
				});
			} finally {
				window.clearTimeout(timer);
			}
		},
		[]
	);

	const loadNavbarData = useCallback(async () => {
		if (navbarCache) {
			setApiData(navbarCache);
			setNavLoading(false);
			return;
		}

		if (navbarRequestedRef.current) {
			return;
		}

		navbarRequestedRef.current = true;
		setNavLoading(true);

		try {
			const response = await fetchWithTimeout(`${SERVER_BASE_URL}/api/navbar`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					brand: "Azure",
				}),
			});

			if (!response.ok) {
				throw new Error("Navbar request failed");
			}

			const result = await response.json();
			const normalized = normalizeNavbarData(result?.data);
			navbarCache = normalized;
			setApiData(normalized);
		} catch {
			setApiData(DEFAULT_NAV_DATA);
		} finally {
			setNavLoading(false);
		}
	}, [fetchWithTimeout]);

	const loadSearchData = useCallback(async () => {
		if (searchCache) {
			setSearchData(searchCache);
			setSearchLoading(false);
			return;
		}

		if (searchRequestedRef.current) {
			return;
		}

		searchRequestedRef.current = true;
		setSearchLoading(true);
		setSearchError("");

		try {
			const response = await fetchWithTimeout(
				`${SERVER_BASE_URL}/api/products/brand`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						brand: "Azure",
					}),
				},
				8000
			);

			if (!response.ok) {
				throw new Error("Products request failed");
			}

			const result = await response.json();
			const products = Array.isArray(result?.data?.products)
				? result.data.products
				: [];

			searchCache = products;
			setSearchData(products);
		} catch {
			searchRequestedRef.current = false;
			setSearchError("Unable to load product search. Please try again.");
		} finally {
			setSearchLoading(false);
		}
	}, [fetchWithTimeout]);

	useEffect(() => {
		loadNavbarData();
	}, [loadNavbarData]);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 50);
		};

		window.addEventListener("scroll", handleScroll, {
			passive: true,
		});

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	const categoryOptions = useMemo(() => {
		const result = {};

		Object.entries(apiData).forEach(([categoryName, categoryValue]) => {
			const years = Array.isArray(categoryValue?.years)
				? [...categoryValue.years].sort((a, b) => Number(b) - Number(a))
				: [];

			const routeCategory = getCategoryRoute(categoryName);

			result[categoryName] = [
				...years.map((year) => ({
					label: `${year} Collection`,
					ariaLabel: `${year} ${categoryName} Collection`,
					value: `/${routeCategory}/${year}`,
				})),
				{
					label: "Size Chart",
					ariaLabel: `${categoryName} Size Chart`,
					value: `/size-chart/${routeCategory}`,
				},
			];
		});

		return result;
	}, [apiData]);

	const categoryNames = useMemo(() => {
		const preferredOrder = ["PROM", "EVENING", "BRIDAL"];
		const keys = Object.keys(categoryOptions);

		return [
			...preferredOrder.filter((name) => keys.includes(name)),
			...keys.filter((name) => !preferredOrder.includes(name)),
		];
	}, [categoryOptions]);

	const openSearch = useCallback(() => {
		setShowSearch(true);
		loadSearchData();
	}, [loadSearchData]);

	const closeSearch = useCallback(() => {
		setShowSearch(false);
	}, []);

	const openMobileMenu = useCallback(() => {
		setShowMenu(true);
	}, []);

	const closeMobileMenu = useCallback(() => {
		setShowMenu(false);
	}, []);

	return (
		<>
			<header
				className={`fixed left-0 z-50 w-full transition-all duration-500 ${
					isScrolled ? "top-[-52px]" : "top-0"
				}`}
			>
				<div
					className={`flex w-full items-center bg-[#1f4c94] px-5 py-3 transition-all duration-500 md:justify-between ${
						isScrolled
							? "pointer-events-none -translate-y-4 scale-95 opacity-0"
							: "translate-y-0 scale-100 opacity-100"
					}`}
				>
					<a
						href="https://www.instagram.com/azuredress?igsh=MXhoNzZ2N3hqbjgyeA=="
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Azure Dress on Instagram"
						className="hidden rounded text-white transition hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:block"
					>
						<InstagramIcon className="h-[22px] w-[22px]" />
					</a>

					<div className="ml-auto hidden items-center gap-3 md:flex">
						<button
							type="button"
							onClick={openSearch}
							aria-label="Open product search"
							className="rounded p-1 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
						>
							<SearchIcon className="h-6 w-6" />
						</button>
					</div>
				</div>

				<nav
					aria-label="Primary navigation"
					className={`border-b border-gray-100 bg-white transition-all duration-500 ease-in-out ${
						isScrolled ? "py-5 shadow-lg" : "py-6"
					}`}
				>
					<div className="flex items-center justify-between px-4 md:justify-center">
						<button
							type="button"
							className="block rounded p-1 md:hidden"
							onClick={openMobileMenu}
							aria-label="Open navigation menu"
							aria-expanded={showMenu}
						>
							<MenuIcon className="h-[35px] w-[35px]" />
						</button>

						<Link
							to="/"
							className="rounded text-center font-americana font-extrabold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94]"
							aria-label="Azure home page"
						>
							<div className="bg-gradient-to-r from-[#3A7BD5] via-[#8A2BE2] to-[#00D2FF] bg-clip-text text-4xl text-transparent">
								Azure
							</div>
							<span className="mt-2 block text-[10px] uppercase tracking-[3px] text-gray-700">
								Proudly Canadian
							</span>
						</Link>

						<button
							type="button"
							onClick={openSearch}
							aria-label="Open product search"
							className="rounded p-1 text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94] md:hidden"
						>
							<SearchIcon className="h-6 w-6" />
						</button>
					</div>

					<div className="hidden pb-3 pt-3 md:block">
						<ul className="hidden flex-wrap items-center justify-center gap-8 font-poppins md:flex">
							<li>
								<Link
									to="/"
									className="rounded px-3 py-2 font-medium text-black hover:text-[#1f4c94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94]"
								>
									HOME
								</Link>
							</li>

							{categoryNames.map((categoryName) => (
								<DropdownMenu
									key={categoryName}
									title={categoryName}
									options={categoryOptions[categoryName] || []}
									loading={navLoading}
								/>
							))}

							<li>
								<Link
									to="/about"
									className="rounded px-3 py-2 font-medium text-black hover:text-[#1f4c94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94]"
								>
									ABOUT US
								</Link>
							</li>

							<li>
								<Link
									to="/contact"
									className="rounded px-3 py-2 font-medium text-black hover:text-[#1f4c94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94]"
								>
									CONTACT
								</Link>
							</li>

							<li className="rounded-md border-2 border-[#1f4c94] p-2 text-[#1f4c94] transition hover:bg-[#1f4c94] hover:text-white">
								<a
									href="https://market.azuredress.com/"
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center gap-1"
								>
									<span className="text-sm font-semibold">RETAILER LOGIN</span>
								</a>
							</li>
						</ul>
					</div>
				</nav>
			</header>

			{showSearch ? (
				<SearchModal
					onClose={closeSearch}
					data={searchData}
					loading={searchLoading}
					error={searchError}
					onRetry={loadSearchData}
				/>
			) : null}

			{showMenu ? (
				<MenuModal
					onClose={closeMobileMenu}
					apiData={apiData}
					navLoading={navLoading}
				/>
			) : null}
		</>
	);
}

function SearchModal({ onClose, data, loading, error, onRetry }) {
	const [searchTerm, setSearchTerm] = useState("");

	const filteredResults = useMemo(() => {
		if (searchTerm.trim().length < 3) {
			return [];
		}

		const term = searchTerm.trim().toLowerCase();

		return data.filter((item) =>
			String(item?.style || "")
				.toLowerCase()
				.includes(term)
		);
	}, [data, searchTerm]);

	return (
		<div
			className="fixed inset-0 z-[1000] flex flex-col items-center justify-start bg-white pt-20"
			role="dialog"
			aria-modal="true"
			aria-label="Product search"
		>
			<button
				type="button"
				onClick={onClose}
				aria-label="Close product search"
				className="absolute right-6 top-6 rounded-full bg-gray-100 p-2 transition hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4c94]"
			>
				<CloseIcon className="h-[35px] w-[35px]" />
			</button>

			<div className="mb-6 flex w-[90%] items-center gap-3 border-b-2 border-gray-400 pb-2 md:w-[60%] lg:w-[40%]">
				<SearchIcon className="h-[30px] w-[30px]" />
				<label htmlFor="azure-search" className="sr-only">
					Search products
				</label>
				<input
					id="azure-search"
					type="search"
					autoComplete="off"
					value={searchTerm}
					onChange={(event) => setSearchTerm(event.target.value)}
					placeholder="Search here..."
					className="w-full text-lg font-medium outline-none"
				/>
			</div>

			{loading ? (
				<LoadingState
					label="Loading products..."
					description="Preparing Azure product search."
					minHeight={300}
				/>
			) : error ? (
				<div className="flex flex-col items-center gap-3 py-12 text-center">
					<p className="text-sm text-red-700">{error}</p>
					<button
						type="button"
						onClick={onRetry}
						className="rounded bg-[#1f4c94] px-4 py-2 text-sm font-semibold text-white"
					>
						Try Again
					</button>
				</div>
			) : (
				<div className="grid max-h-[80vh] w-[90%] grid-cols-1 gap-4 overflow-y-auto pb-10 sm:grid-cols-2 md:w-[70%] lg:grid-cols-3">
					{filteredResults.length > 0 ? (
						filteredResults.map((item) => (
							<Featurelist
								key={item?._id || item?.id || item?.style}
								data={item}
							/>
						))
					) : searchTerm.length >= 3 ? (
						<p className="w-full text-center text-gray-600">No results found.</p>
					) : (
						<p className="w-full text-center text-gray-500">
							Type at least 3 letters to search
						</p>
					)}
				</div>
			)}
		</div>
	);
}

function MenuModal({ onClose, apiData, navLoading }) {
	const [openDropdown, setOpenDropdown] = useState(null);

	const toggleDropdown = useCallback((menuName) => {
		setOpenDropdown((current) => (current === menuName ? null : menuName));
	}, []);

	const categories = useMemo(() => {
		return Object.entries(apiData);
	}, [apiData]);

	return (
		<>
			<button
				type="button"
				className="fixed inset-0 z-[999] bg-black/40"
				onClick={onClose}
				aria-label="Close navigation menu overlay"
			/>

			<div
				className="fixed left-0 top-0 z-[1000] h-full w-[80%] overflow-y-auto bg-white shadow-2xl md:w-[40%]"
				role="dialog"
				aria-modal="true"
				aria-label="Mobile navigation"
			>
				<div className="flex items-center justify-between border-b px-5 py-4">
					<div>
						<h2 className="text-2xl font-bold text-[#1f4c94]">Azure</h2>
						<span className="text-[9px] uppercase tracking-[2px] text-gray-600">
							Proudly Canadian
						</span>
					</div>

					<button
						type="button"
						onClick={onClose}
						aria-label="Close navigation menu"
						className="rounded-full bg-gray-100 p-2 hover:bg-gray-200"
					>
						<CloseIcon className="h-[35px] w-[35px]" />
					</button>
				</div>

				<ul className="mt-5 flex flex-col gap-4 px-4 font-poppins text-[16px] font-medium text-black">
					<li className="px-2">
						<Link to="/" onClick={onClose}>
							HOME
						</Link>
					</li>

					{navLoading ? (
						<li className="px-2 text-sm text-gray-500">Loading collections...</li>
					) : null}

					{categories.map(([categoryName, categoryValue]) => {
						const categoryKey = getCategoryRoute(categoryName);
						const years = Array.isArray(categoryValue?.years)
							? [...categoryValue.years].sort((a, b) => Number(b) - Number(a))
							: [];

						return (
							<li className="px-2" key={categoryName}>
								<button
									type="button"
									onClick={() => toggleDropdown(categoryKey)}
									className="flex w-full items-center gap-2 text-left font-medium hover:text-[#1f4c94]"
									aria-expanded={openDropdown === categoryKey}
								>
									{categoryName}
									<ChevronDownIcon
										className={`h-4 w-4 transition ${
											openDropdown === categoryKey ? "rotate-180" : ""
										}`}
									/>
								</button>

								{openDropdown === categoryKey ? (
									<ul className="ml-5 mt-2 flex flex-col gap-4">
										{years.map((year) => (
											<li key={year}>
												<Link
													to={`/${categoryKey}/${year}`}
													onClick={onClose}
												>
													{year} Collection
												</Link>
											</li>
										))}
										<li>
											<Link
												to={`/size-chart/${categoryKey}`}
												onClick={onClose}
											>
												Size Chart
											</Link>
										</li>
									</ul>
								) : null}
							</li>
						);
					})}

					<li className="px-2">
						<Link to="/about" onClick={onClose}>
							ABOUT US
						</Link>
					</li>

					<li className="px-2">
						<Link to="/contact" onClick={onClose}>
							CONTACT
						</Link>
					</li>

					<li>
						<a
							href="https://market.azuredress.com/"
							target="_blank"
							rel="noopener noreferrer"
							onClick={onClose}
							className="flex items-center gap-1 bg-[#1f4c94] px-2 py-3 text-white"
						>
							<span className="font-medium">RETAILER LOGIN</span>
						</a>
					</li>
				</ul>
			</div>
		</>
	);
}

export default memo(Navbar);
